import { ScopeButton } from '@monorepo/shared-lib';
import { Row, Select, Space, Typography } from 'antd';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { turnModalOff } from 'store/app/actions';
import type { Organisation } from 'store/organisation/models';
import { filterMapSelectOption } from './extraction-rules-styles';
import type { InterpretationRule } from './types';

const { Text } = Typography;

export interface CopyRuleToEntityModalProps {
  rule: InterpretationRule;
  targetOrganisations: Organisation[];
  onContinue: (targetOrganisationId: string) => void;
}

export const CopyRuleToEntityModal: React.FC<CopyRuleToEntityModalProps> = ({
  rule,
  targetOrganisations,
  onContinue,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedOrganisationId, setSelectedOrganisationId] = React.useState<
    string | undefined
  >(undefined);
  const [continuing, setContinuing] = React.useState(false);

  const ruleName =
    rule.name?.trim() ||
    t('organisation.extraction-rules.default-rule-name');

  const entityOptions = React.useMemo(
    () =>
      targetOrganisations.map((org) => ({
        value: org.uuid,
        label: org.friendlyName,
        providerName: org.providerName,
      })),
    [targetOrganisations]
  );

  const handleContinue = () => {
    if (!selectedOrganisationId || continuing) return;
    setContinuing(true);
    onContinue(selectedOrganisationId);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        width: '100%',
        maxWidth: 480,
        margin: '0 auto',
        padding: '8px 0 4px',
      }}
    >
      <Text style={{ color: 'var(--color-text-muted, #5a6a6a)', fontSize: 13, textAlign: 'center' }}>
        {t('organisation.extraction-rules.copy-to-description', {
          ruleName,
        })}
      </Text>

      {targetOrganisations.length === 0 ? (
        <Text
          style={{
            color: 'var(--color-text-muted, #5a6a6a)',
            fontSize: 13,
            textAlign: 'center',
            padding: '12px 0',
          }}
        >
          {t('organisation.extraction-rules.copy-to-no-entities')}
        </Text>
      ) : (
        <Select
          showSearch
          placeholder={t('organisation.extraction-rules.copy-to-select-entity')}
          value={selectedOrganisationId}
          onChange={setSelectedOrganisationId}
          options={entityOptions}
          optionLabelProp="label"
          filterOption={filterMapSelectOption}
          style={{ width: '100%' }}
          size="large"
          optionRender={(option) => {
            return (
              <Space size={8}>
                <ProviderIcon
                  providerName={option.data.providerName as string | undefined}
                  alt=""
                  width={18}
                  height={18}
                  style={{ flexShrink: 0 }}
                />
                <span>{option.label}</span>
              </Space>
            );
          }}
        />
      )}

      <Row justify="center">
        <Space>
          <ScopeButton
            type="default"
            width="100px"
            height="40px"
            onClick={() => dispatch(turnModalOff())}
          >
            {t('common.cancel')}
          </ScopeButton>
          <ScopeButton
            type="primary"
            width="fit-content"
            height="40px"
            disabled={!selectedOrganisationId || continuing}
            onClick={handleContinue}
          >
            {t('common.continue')}
          </ScopeButton>
        </Space>
      </Row>
    </div>
  );
};
